import {useEffect, useRef, useState} from "react";
import _events_service from "../../service/EventsService";
import _patreon_service from "../../service/PatreonService";
import BouncyText from "../../components/bouncy-text/BouncyText";
import NameReplaceService from "../../service/NameReplaceService";
import './CreditsOverlay.scss';

interface CreditsOverlayState {
    followers: string[],
    subscribers: string[],
    gifts: { [username: string]: number },
    cheers: { [username: string]: number },
    raids: { [username: string]: number },
    donations: { [username: string]: number },
    patrons: string[]
}

const CreditsOverlay = () => {

    const initialState = {followers: [], subscribers: [], gifts: {}, cheers: {}, raids: {}, donations: {},
        sponsorPatrons: [], supporterPatrons: [], patrons: []};

    const credits = useRef<CreditsOverlayState>({...initialState});
    const [fetchedEvents, setFetchedEvents] = useState<boolean>(false);
    const [fetchedPatrons, setFetchedPatrons] = useState<boolean>(false);

    useEffect(() => {
        _events_service.getAllEvents().then(events => {
            events.forEach(event => {
                switch (event.type) {
                    case 'follow':
                        credits.current.followers.push(event.username);
                        break;
                    case 'sub':
                        credits.current.subscribers.push(event.username);
                        break;
                    case 'gift':
                        if (!credits.current.gifts[event.username]) {
                            credits.current.gifts[event.username] = 0;
                        }
                        credits.current.gifts[event.username] += event.count || 1;
                        break;
                    case 'cheer':
                        if (!credits.current.cheers[event.username]) {
                            credits.current.cheers[event.username] = 0;
                        }
                        credits.current.cheers[event.username] += event.count || 1;
                        break;
                    case 'raid':
                        credits.current.raids[event.username] = event.count || 1;
                        break;
                    case 'donation':
                        if (!credits.current.donations[event.username]) {
                            credits.current.donations[event.username] = 0;
                        }
                        credits.current.donations[event.username] += (event.count! / 100.0);
                        break;
                    default:
                        console.log('unknown event type: ' + event.type);
                }
            });
        }).finally(() => setFetchedEvents(true));

        _patreon_service.getPatrons().then(list => {
            Object.keys(list).forEach(tier => {
                switch (tier) {
                    case 'Sponsor Tier':
                    case 'Supporter Tier':
                    case 'Patron Tier':
                        credits.current.patrons = [...credits.current.patrons, ...list[tier]];
                        break;
                    case 'Free Tier':
                    default:
                        console.log('tier listed with value: ' + tier);
                }
            });
        }).finally(() => setFetchedPatrons(true));
    }, []);

    return (fetchedEvents && fetchedPatrons) ? <>
        <div className="credits-overlay">
            <div className="credits-overlay-text">
                <h1>Credits</h1>
                {fetchedEvents && credits.current.followers.length > 0 && <>
                    <h2>New Followers</h2>
                    {credits.current.followers
                        .sort()
                        .map(follower => <p key={`follower-${follower}`}>{follower}</p>)}
                </>}
                {fetchedEvents && Object.keys(credits.current.raids).length > 0 && <>
                    <h2>Raiders</h2>
                    {Object.keys(credits.current.raids)
                        .sort((a, b) => credits.current.raids[b] - credits.current.raids[a])
                        .map(raider => <p key={`raider-${raider}`}>{raider} - {credits.current.raids[raider]}</p>)}
                </>}
                {fetchedEvents && Object.keys(credits.current.gifts).length > 0 && <>
                    <h2>Gifters</h2>
                    {Object.keys(credits.current.gifts)
                        .sort((a, b) => credits.current.gifts[b] - credits.current.gifts[a])
                        .map(gifter => <p key={`gifter-${gifter}`}>{gifter} - {credits.current.gifts[gifter]}</p>)}
                </>}
                {fetchedEvents && Object.keys(credits.current.cheers).length > 0 && <>
                    <h2>Cheerers</h2>
                    {Object.keys(credits.current.cheers)
                        .sort((a, b) => credits.current.cheers[b] - credits.current.cheers[a])
                        .map(cheerer => <p key={`cheer-${cheerer}`}>{cheerer} - {credits.current.cheers[cheerer]}</p>)}
                </>}
                {fetchedEvents && credits.current.subscribers.length > 0 && <>
                    <h2>Subscribers</h2>
                    {credits.current.subscribers
                        .sort()
                        .map(subscriber => <p key={`sub-${subscriber}`}>{subscriber}</p>)}
                </>}
                {fetchedEvents && Object.keys(credits.current.donations).length > 0 && <>
                    <h2>Charitable Donations</h2>
                    {Object.keys(credits.current.donations)
                        .sort((a, b) => credits.current.donations[b] - credits.current.donations[a])
                        .map(donor => <p key={`donor-${donor}`}>{donor} - {credits.current.donations[donor]}</p>)}
                </>}
                {fetchedPatrons && credits.current.patrons.length > 0 && <>
                    <h2><BouncyText className="rainbow" text={'Thank You, Patreon Members!'}/></h2>
                    {credits.current.patrons
                        .map(patron => NameReplaceService.replace(patron))
                        .sort((a, b) => a.localeCompare(b, [], {sensitivity: 'base'}))
                        .map(patron => <p key={`sponsor-${patron}`}>{patron}</p>)
                    }
                </>}
                <h2>
                    Join my Patreon to see your name here!<br/>
                    <span className="blue">patreon.com/chasethefox</span>
                </h2>
            </div>
        </div>
    </> : <></>;
}

export default CreditsOverlay;